import React, { Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import styles from './Auth.module.scss';
import { apiService } from '../../services/api';
import { firebaseAuthService } from '../../services/firebaseAuth';
import { registerPushToken } from '../../services/push';
import { getVapidKey } from '../../config/environment';
import OTPInput from '../../components/molecules/OTPInput/OTPInput';
import GoogleSignIn from '../../components/Auth/GoogleSignIn';
import { getGoogleClientId } from '../../config/environment';

const getIsCustomLoginFromUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.has('customLogin') ||
    params.get('customLogin') === 'true' ||
    params.has('isCustomLogin') ||
    params.get('isCustomLogin') === 'true'
  );
};

/** Use current URL so it works on direct load and client-side navigation (e.g. after deploy). */
export function getIsCustomLogin(): boolean {
  return getIsCustomLoginFromUrl();
}

interface LoginState {
  authMethod: 'email' | 'phone';
  formData: {
    email: string;
    password: string;
    phone: string;
    otp: string;
    rememberMe: boolean;
  };
  errors: {[key: string]: string};
  isLoading: boolean;
  isGoogleProcessing: boolean;
  googleProcessingStep: string;
  otpSent: boolean;
  countdown: number;
  otpComplete: boolean;
  currentOtp: string;
}

// Higher-order component to provide navigation
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class Login extends Component<any, LoginState> {
  private countdownInterval: NodeJS.Timeout | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      authMethod: 'email',
      formData: {
        email: '',
        password: '',
        phone: '',
        otp: '',
        rememberMe: false
      },
      errors: {},
      isLoading: false,
      isGoogleProcessing: false,
      googleProcessingStep: '',
      otpSent: false,
      countdown: 0,
      otpComplete: false,
      currentOtp: ''
    };
  }

  componentDidMount() {
    // Validate Google Client ID on mount
    const clientId = getGoogleClientId();
    if (!clientId || clientId.includes('your-') || clientId.length < 20) {
      console.warn('[Login] Google Client ID may be invalid:', clientId);
      console.warn('[Login] Please ensure VITE_GOOGLE_CLIENT_ID is set correctly in your .env file');
    }
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    firebaseAuthService.cleanup();
  }

  /** Register FCM push token with backend (fire-and-forget after login). VAPID from API first, then env. */
  registerPushTokenAfterLogin = () => {
    apiService
      .getFirebaseVapidKey()
      .then((res) => res.data?.value || getVapidKey())
      .catch(() => getVapidKey())
      .then((vapid) => {
        if (!vapid?.trim()) return;
        return registerPushToken(vapid);
      })
      .then((token) => {
        if (token) {
          return apiService
            .registerPushToken({ fcmToken: token, platform: 'web' })
            .then((res) => {
              if (res?.success) console.log('[FCM] Push token registered with backend (customer)');
              return res;
            });
        }
      })
      .catch(() => {});
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
    
    // Clear error when user starts typing
    if (this.state.errors[name]) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, [name]: '' }
      }));
    }
  };

  handleAuthMethodChange = (method: 'email' | 'phone') => {
    this.setState({
      authMethod: method,
      formData: {
        ...this.state.formData,
        phone: '',
        otp: '',
        email: method === 'email' ? this.state.formData.email : '',
        password: method === 'email' ? this.state.formData.password : ''
      },
      errors: {},
      otpSent: false,
      countdown: 0
    });
    firebaseAuthService.cleanup();
  };

  handleSendOTP = async () => {
    const { phone } = this.state.formData;
    
    if (!phone) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: 'Phone number is required' }
      }));
      return;
    }

    // Validate phone number format
    if (!/^\+?[\d\s\-\(\)]{10,}$/.test(phone)) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: 'Please enter a valid phone number (e.g., +1234567890)' }
      }));
      return;
    }

    this.setState({ isLoading: true, errors: {} });

    try {
        // Debug: Check Firebase configuration - Show actual values
        if (typeof window !== 'undefined' && import.meta.env.DEV) {
          console.log('Firebase Config Check - Actual Values:', {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "FALLBACK: AIzaSyCVZy9etMQWG__UBtgxTnGOIZ28buCDn14",
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "FALLBACK: cue-live-50585.firebaseapp.com",
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "FALLBACK: cue-live-50585",
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "FALLBACK: cue-live-50585.firebasestorage.app",
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "FALLBACK: 132372805332",
            appId: import.meta.env.VITE_FIREBASE_APP_ID || "FALLBACK: 1:132372805332:web:d59d58a6170c645602ed41",
            measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "FALLBACK: G-TYJSCMDT7E",
            allEnvVars: Object.keys(import.meta.env).filter(k => k.startsWith('VITE_FIREBASE')),
            isUsingFallback: {
              apiKey: !import.meta.env.VITE_FIREBASE_API_KEY,
              authDomain: !import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
              projectId: !import.meta.env.VITE_FIREBASE_PROJECT_ID
            }
          });
        }


      // Ensure reCAPTCHA container exists (create if doesn't exist)
      let container = document.getElementById('recaptcha-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'recaptcha-container';
        container.style.display = 'none';
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        document.body.appendChild(container);
      }

      // Wait a moment for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      await firebaseAuthService.initializeRecaptcha('recaptcha-container');
      
      const result = await firebaseAuthService.sendOTP(phone);
      
      if (result.success) {
      this.setState({ 
        otpSent: true, 
        countdown: 60,
        errors: {},
        otpComplete: false,
        currentOtp: ''
      });
        
        // Start countdown timer
        this.countdownInterval = setInterval(() => {
          this.setState(prevState => {
            if (prevState.countdown <= 1) {
              if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
              }
              return { countdown: 0 };
            }
            return { countdown: prevState.countdown - 1 };
          });
        }, 1000);
      } else {
        this.setState(prevState => ({
          errors: { ...prevState.errors, phone: result.message }
        }));
      }
    } catch (error: any) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: error.message || 'Failed to send OTP' }
      }));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleOTPChange = (otp: string) => {
    // Track OTP changes for manual submit button
    this.setState({
      currentOtp: otp,
      otpComplete: otp.length === 6
    });
  };

  handleOTPComplete = async (otp: string) => {
    // Auto-submit when all digits are entered
    await this.verifyOTP(otp);
  };

  verifyOTP = async (otp?: string) => {
    const otpToVerify = otp || this.state.currentOtp;
    
    if (otpToVerify.length !== 6) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, otp: 'Please enter all 6 digits' }
      }));
      return;
    }

    this.setState({ isLoading: true, errors: {} });

    try {
      // Verify OTP with Firebase
      const result = await firebaseAuthService.verifyOTP(otpToVerify);
      
      if (result.success && result.user) {
        // Get Firebase ID token
        const idToken = await firebaseAuthService.getIdToken();
        
        if (!idToken) {
          throw new Error('Failed to get Firebase authentication token');
        }

        const phoneNumber = result.user.phoneNumber || this.state.formData.phone;
        
        // Send Firebase ID token to backend for authentication
        try {
          const response = await apiService.firebasePhoneAuth(idToken, phoneNumber);
          
          if (response.success && response.data) {
            if (response.data.accessToken) {
              // Store tokens from backend
              apiService.setTokens(
                response.data.accessToken,
                response.data.refreshToken
              );
              this.registerPushTokenAfterLogin();

              // Clear any previous errors
              this.setState({ errors: {} });
              
              // Verify profile after successful mobile verification
              let isProfileComplete = false;
              try {
                const verifyResponse = await apiService.verifyCustomerProfile();
                if (verifyResponse.success && verifyResponse.data) {
                  isProfileComplete = verifyResponse.data.isProfileComplete ?? isProfileComplete;
                  console.log('Profile verified successfully after mobile verification');
                }
              } catch (verifyError: any) {
                console.error('Profile verification failed:', verifyError);
                // Continue with login even if verification fails, use original isProfileComplete
              }
              
              console.log('Phone login successful:', response.data.user);
              console.log('Profile complete:', isProfileComplete);
              
              // Check for stored redirect path
              const redirectPath = sessionStorage.getItem('redirectAfterLogin');
              if (redirectPath && isProfileComplete === true) {
                sessionStorage.removeItem('redirectAfterLogin');
                console.log('[Login] Redirecting to stored path after phone login:', redirectPath);
                this.props.navigate(redirectPath);
                return;
              }
              
              // If profile is complete, go to friends page; otherwise, go to profile page
              if (isProfileComplete === true) {
                console.log('Profile is complete, navigating to friends page...');
                this.props.navigate('/customer/friends');
              } else {
                console.log('Profile is incomplete, navigating to profile page...');
                this.props.navigate('/profile');
              }
            } else {
              throw new Error('No access token found in response');
            }
          } else {
            throw new Error(response.message || 'Phone authentication failed');
          }
        } catch (apiError: any) {
          console.error('Backend phone authentication failed:', apiError);
          
          // Check if it's a network error (backend not running)
          if (apiError.message.includes('Unable to connect to server')) {
            // For testing purposes, allow login when backend is down
            console.log('Backend not available, using test mode');
            apiService.setTokens('test-access-token', 'test-refresh-token');
            this.setState({ errors: {} });
            this.props.navigate('/profile');
            return;
          }
          
          throw new Error(apiError.message || 'Backend authentication failed');
        }
      } else {
        this.setState(prevState => ({
          errors: { ...prevState.errors, otp: result.message }
        }));
      }
    } catch (error: any) {
      console.error('OTP verification failed:', error);
      this.setState(prevState => ({
        errors: { ...prevState.errors, otp: error.message || 'Failed to verify OTP' }
      }));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!this.state.formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(this.state.formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!this.state.formData.password) {
      newErrors.password = 'Password is required';
    } else if (this.state.formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted, navigate function available:', !!this.props.navigate);
    
    if (!this.validateForm()) return;

    this.setState({ isLoading: true });
    
    try {
      console.log('Attempting login with:', { email: this.state.formData.email });
      
      // Call the real login API with identifier payload (email as identifier)
      const response = await apiService.login({
        identifier: this.state.formData.email,
        identifierType: 'email',
        password: this.state.formData.password
      });
      
      console.log('API Response:', response);
      console.log('Response structure:', {
        success: response.success,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : 'no data',
        hasAccessToken: response.data?.accessToken ? 'yes' : 'no',
        hasRefreshToken: response.data?.refreshToken ? 'yes' : 'no',
        hasUser: response.data?.user ? 'yes' : 'no'
      });
      
      if (response.success && response.data) {
        // Check if tokens exist in the response (they are directly in data, not nested under tokens)
        if (response.data.accessToken) {
          // Store tokens in localStorage
          apiService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
          this.registerPushTokenAfterLogin();

          // Handle successful login
          console.log('Login successful:', response.data.user);
          
          // Check profile completion
          const isProfileComplete = response.data.user?.isProfileComplete ?? false;
          console.log('Profile complete:', isProfileComplete);
          
          // Clear any previous errors
          this.setState({ errors: {} });
          
          // Check if there's a stored redirect URL (e.g., from shared post link)
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath) {
            // Clear the stored redirect path
            sessionStorage.removeItem('redirectAfterLogin');
            console.log('[Login] Redirecting to stored path after login:', redirectPath);
            // Only redirect if profile is complete, otherwise go to profile first
            if (isProfileComplete === true) {
              this.props.navigate(redirectPath);
              return;
            } else {
              // Store it again to redirect after profile completion
              sessionStorage.setItem('redirectAfterLogin', redirectPath);
              console.log('[Login] Profile incomplete, will redirect after profile completion');
            }
          }
          
          // Navigate based on profile completion
          // If profile is complete, go to friends page; otherwise, go to profile page
          if (isProfileComplete === true) {
            console.log('Profile is complete, navigating to friends page...');
            this.props.navigate('/customer/friends');
          } else {
            console.log('Profile is incomplete, navigating to profile page...');
            this.props.navigate('/profile');
          }
        } else {
          // Fallback: if no accessToken but user data exists, use test mode
          if (response.data.user) {
            console.log('No accessToken found, using test mode');
            const isProfileComplete = response.data.user?.isProfileComplete ?? false;
            apiService.setTokens('test-access-token', 'test-refresh-token');
            this.setState({ errors: {} });
            
            // Check for stored redirect path
            const redirectPath = sessionStorage.getItem('redirectAfterLogin');
            if (redirectPath && isProfileComplete === true) {
              sessionStorage.removeItem('redirectAfterLogin');
              this.props.navigate(redirectPath);
              return;
            }
            
            // If profile is complete, go to friends page; otherwise, go to profile page
            if (isProfileComplete === true) {
              this.props.navigate('/customer/friends');
            } else {
              this.props.navigate('/profile');
            }
            return;
          }
          
          throw new Error('No access token found in response');
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
      
    } catch (error: any) {
      console.error('Login failed:', error);
      
      const errorMessage = error?.message || 'Login failed';
      
      // Check if it's a network error (backend not running) or timeout
      if (errorMessage.includes('Unable to connect to server') || 
          errorMessage.includes('Request timeout') ||
          errorMessage.includes('took too long to respond')) {
        // For testing purposes, allow login with any credentials when backend is down
        console.log('Backend not available or timed out, using test mode');
        
        // Store dummy tokens for testing
        apiService.setTokens('test-access-token', 'test-refresh-token');
        
        // Clear any previous errors
        this.setState({ errors: {} });
        
        // Navigate to profile for testing (profile completion required)
        console.log('Test mode: Navigating to profile...');
        this.props.navigate('/profile');
        console.log('Test mode: Navigation called');
        return;
      }
      
      // Show error message to user
      this.setState({
        errors: {
          ...this.state.errors,
          general: error.message || 'Login failed. Please check your credentials and try again.'
        }
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleGoogleSuccess = async (credentialResponse: any) => {
    const credential = credentialResponse.credential;
    
    try {
      const userInfo = jwtDecode(credential) as any;
      console.log('Google user info:', userInfo);
    } catch (err) {
      console.error('Failed to decode JWT:', err);
    }

    this.setState({ isLoading: true, isGoogleProcessing: true, googleProcessingStep: 'Verifying your Google account…' });

    try {
      this.setState({ googleProcessingStep: 'Signing you in…' });
      const response = await apiService.googleAuth(credential);
      
      if (response.success && response.data) {
        if (response.data.accessToken) {
          apiService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
          this.registerPushTokenAfterLogin();

          this.setState({ errors: {}, googleProcessingStep: 'Checking your profile…' });
          
          let isProfileComplete = false;
          try {
            const verifyResponse = await apiService.verifyCustomerProfile();
            if (verifyResponse.success && verifyResponse.data) {
              isProfileComplete = verifyResponse.data.isProfileComplete ?? isProfileComplete;
              console.log('Profile verified successfully after Gmail verification');
            }
          } catch (verifyError: any) {
            console.error('Profile verification failed:', verifyError);
          }

          console.log('Google login successful:', response.data.user);
          console.log('Profile complete:', isProfileComplete);

          this.setState({ googleProcessingStep: 'Redirecting…' });
          
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath && isProfileComplete === true) {
            sessionStorage.removeItem('redirectAfterLogin');
            console.log('[Login] Redirecting to stored path after Google login:', redirectPath);
            this.props.navigate(redirectPath);
            this.setState({ isLoading: false, isGoogleProcessing: false, googleProcessingStep: '' });
            return;
          }
          
          if (isProfileComplete === true) {
            console.log('Profile is complete, navigating to friends page...');
            this.props.navigate('/customer/friends');
          } else {
            console.log('Profile is incomplete, navigating to profile page...');
            this.props.navigate('/profile');
          }
          
          this.setState({ isLoading: false, isGoogleProcessing: false, googleProcessingStep: '' });
        } else {
          throw new Error('No access token found in response');
        }
      } else {
        throw new Error(response.message || 'Google login failed');
      }
    } catch (error: any) {
      console.error('Google login failed:', error);
      
      if (error.message.includes('Unable to connect to server') || error.message.includes('Failed to fetch') || error.message.includes('Request timeout')) {
        console.log('[Login] Backend auth/google request failed – ensure backend is running (e.g. localhost:3010)');
        this.setState({
          errors: {
            ...this.state.errors,
            general: 'We tried to sign you in with the backend but the request failed. Ensure the backend server is running (e.g. at the API URL in .env) and try again.'
          },
          isLoading: false,
          isGoogleProcessing: false,
          googleProcessingStep: ''
        });
        return;
      }
      
      this.setState({
        errors: {
          ...this.state.errors,
          general: error.message || 'Google login failed. Please try again.'
        },
        isLoading: false,
        isGoogleProcessing: false,
        googleProcessingStep: ''
      });
    }
  };

  handleGoogleError = (errorInfo?: any) => {
    console.error('Google Login Failed:', errorInfo);
    
    let errorMessage = 'Google login was cancelled or failed. Please try again.';
    
    // Handle different error types with more specific messages
    if (errorInfo) {
      if (errorInfo.type === 'user_cancelled') {
        errorMessage = 'Sign-in was cancelled. Please try again when ready.';
      } else if (errorInfo.type === 'network_error') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (errorInfo.type === 'configuration_error') {
        errorMessage = 'Google Sign-In is not properly configured. Please contact support.';
      } else if (errorInfo.type === 'plugin_unavailable') {
        errorMessage = 'Google Sign-In is not available on this device. Please use email or phone login.';
      } else if (errorInfo.type === 'initialization_error') {
        errorMessage = 'Failed to initialize Google Sign-In. Please try again or use email/phone login.';
      } else if (errorInfo.message) {
        errorMessage = errorInfo.message;
      }
    }
    
    this.setState({
      errors: {
        ...this.state.errors,
        general: errorMessage
      },
      isLoading: false,
      isGoogleProcessing: false,
      googleProcessingStep: ''
    });
  };

  render() {
    const { formData, errors, isLoading, isGoogleProcessing, googleProcessingStep } = this.state;
    
    return (
      <div className={styles.authContainer}>
        <div className={styles.authBackground}>
          <div className={styles.poolTable}>
            <div className={styles.poolBalls}>
              <div className={styles.ball}></div>
              <div className={styles.ball}></div>
              <div className={styles.ball}></div>
            </div>
          </div>
        </div>

        <div className={styles.authFormContainer}>
          <div className={styles.authForm} style={{ position: 'relative' }}>
            {/* Google Processing Overlay */}
            {isGoogleProcessing && (
              <div className={styles.processingOverlay}>
                <div className={styles.processingSpinner} />
                <span className={styles.processingText}>{googleProcessingStep}</span>
                <span className={styles.processingSubtext}>Please wait, do not close this page</span>
              </div>
            )}

            {/* Logo */}
            <div className={styles.logo}>
              <img 
                src="/images/chatup24-logo.png" 
                alt="Chatup24" 
                className={styles.logoImage}
              />
            </div>

            {/* Header */}
            <div className={styles.authHeader}>
              <h1 className={styles.authTitle}>Welcome Back</h1>
              <p className={styles.authSubtitle}>Sign in to your Chatup24 account</p>
            </div>

            {/* Auth Method Toggle - hidden when isCustomLogin is false */}
            {getIsCustomLogin() && (
            <div className={styles.authMethodToggle}>
              <button
                type="button"
                className={`${styles.toggleButton} ${this.state.authMethod === 'email' ? styles.active : ''}`}
                onClick={() => this.handleAuthMethodChange('email')}
                disabled={this.state.isLoading}
              >
                Email
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${this.state.authMethod === 'phone' ? styles.active : ''}`}
                onClick={() => this.handleAuthMethodChange('phone')}
                disabled={this.state.isLoading}
              >
                Phone
              </button>
            </div>
            )}

            {/* Form - hidden when isCustomLogin is false */}
            {getIsCustomLogin() && (this.state.authMethod === 'email' ? (
            <form onSubmit={this.handleSubmit} className={styles.form} >
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={this.handleInputChange}
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={this.handleInputChange}
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                {errors.password && <span className={styles.errorText}>{errors.password}</span>}
              </div>

              <div className={styles.formOptions}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={this.handleInputChange}
                    className={styles.checkbox}
                    disabled={isLoading}
                  />
                  <span className={styles.checkboxText}>Remember me</span>
                </label>
                
                <Link to="/forgot-password" className={styles.forgotLink}>
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className={styles.loadingSpinner}></span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            ) : (
            <div className={styles.form}>
              {!this.state.otpSent ? (
                <>
                  <div className={styles.formGroup}>
                    <label htmlFor="phone" className={styles.label}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={this.state.formData.phone}
                      onChange={this.handleInputChange}
                      className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                      placeholder="+1234567890"
                      disabled={isLoading}
                    />
                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                    <p className={styles.helperText}>
                      Enter your phone number with country code (e.g., +1234567890)
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={this.handleSendOTP}
                    className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className={styles.loadingSpinner}></span>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Enter OTP
                    </label>
                    <OTPInput
                      length={6}
                      onComplete={this.handleOTPComplete}
                      onChange={this.handleOTPChange}
                      disabled={isLoading}
                      error={errors.otp}
                    />
                    {errors.otp && <span className={styles.errorText}>{errors.otp}</span>}
                  </div>

                  <button
                    type="button"
                    onClick={() => this.verifyOTP()}
                    className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                    disabled={isLoading || !this.state.otpComplete}
                  >
                    {isLoading ? (
                      <span className={styles.loadingSpinner}></span>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>

                  <div className={styles.otpActions}>
                    <button
                      type="button"
                      onClick={this.handleSendOTP}
                      className={styles.resendButton}
                      disabled={isLoading || this.state.countdown > 0}
                    >
                      {this.state.countdown > 0 
                        ? `Resend OTP in ${this.state.countdown}s` 
                        : 'Resend OTP'}
                    </button>
                    <button
                      type="button"
                      onClick={() => this.setState({ otpSent: false, countdown: 0, otpComplete: false, currentOtp: '' })}
                      className={styles.changeNumberButton}
                      disabled={isLoading}
                    >
                      Change Number
                    </button>
                  </div>
                </>
              )}
            </div>
            ))}

            {/* reCAPTCHA Container (hidden) */}
            <div id="recaptcha-container"></div>

            {/* General Error Message */}
            {errors.general && (
              <div className={styles.errorMessage}>
                {errors.general}
              </div>
            )}           

            {/* Divider - show whenever we show the Google button (always) */}
            <div className={styles.divider}>
              <span>or</span>
            </div>

            {/* Social Login - Gmail / Google sign-in (always visible) */}
            <div className={styles.socialLogin}>
              <GoogleSignIn
                onSuccess={this.handleGoogleSuccess}
                onError={this.handleGoogleError}
                useOneTap
              />
            </div>

            {/* Sign Up Link */}
            {getIsCustomLogin() && (
            <div className={styles.authFooter}>
              <p>
                Don't have an account?{' '}
                <Link to="/signup" className={styles.authLink}>
                  Sign up for free
                </Link>
              </p>
            </div>  
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withNavigation(Login);
